import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import '../App.css';
import EmployeeList from './EmployeeList';
import BookingModal from './BookingModal';
import { getEmployees } from '../services/api';

function Home({ user, isAuthenticated, onLogout }) {
  const navigate = useNavigate();
  const [allEmployees, setAllEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [expertType, setExpertType] = useState('');
  const [loading, setLoading] = useState(true);

  const loadEmployees = useCallback(async () => {
    setLoading(true);
    try {
      console.log('🔄 Loading employees...');
      const data = await getEmployees();
      console.log('✅ Employees loaded:', data.length);
      console.log('📋 Employee names:', data.map(e => e.name));
      setAllEmployees(data);
    } catch (error) {
      console.error('❌ Error loading employees:', error);
      console.error('Error details:', error.response?.data || error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  // Filter employees based on expertType
  useEffect(() => {
    console.log('🔍 Filtering employees. Total:', allEmployees.length, 'Expert Type:', expertType);
    if (allEmployees.length === 0) {
      console.log('⚠️ No employees to filter');
      setFilteredEmployees([]);
      return;
    }
    
    // If no expert type selected, show all employees
    if (!expertType || expertType === '') {
      console.log('✅ Showing all employees (no filter)');
      setFilteredEmployees(allEmployees);
      return;
    }
    
    // Map expert types to employee name patterns
    const expertTypeMap = {
      'Psychiatrist': ['Priyanka Kalra', 'Kalra'],
      'Child and Youth Expert': ['Mitali', 'Prithvi', 'Priyanka Leekha', 'Ramandeep'],
      'Couple Therapist': ['Sunil', 'Ritu', 'Mitali', 'Vanita'],
      'Career Counseling': ['Mitali', 'Priyanka Leekha', 'Ramandeep']
    };
    
    const namePatterns = expertTypeMap[expertType] || [];
    
    const filtered = allEmployees.filter(employee => {
      if (!employee.name) return false;
      
      // Check if employee name matches any pattern
      return namePatterns.some(pattern => 
        employee.name.toLowerCase().includes(pattern.toLowerCase())
      );
    });
    
    console.log(`Filtered ${filtered.length} employees for expert type: ${expertType}`);
    console.log('Name patterns:', namePatterns);
    console.log('Filtered employees:', filtered.map(e => e.name + ' - ' + e.title));
    setFilteredEmployees(filtered);
  }, [expertType, allEmployees]);

  const handleBookClick = (employee) => {
    if (!isAuthenticated) {
      // Redirect to login with return path
      navigate(`/login?redirect=/employee/${employee._id}`, {
        state: {
          from: `/employee/${employee._id}`,
          message: 'Please login to book a session'
        }
      });
      return;
    }
    setSelectedEmployee(employee);
    setShowBookingModal(true);
  };

  const handleBookingSuccess = () => {
    setShowBookingModal(false);
    setSelectedEmployee(null);
    loadEmployees();
  };

  return (
    <div className="app">
      <main className="main-content">
        <div className="breadcrumbs">
          <span>Home / Employees</span>
        </div>

        <div className="hero-section">
          <h2 className="hero-title">Find an expert who understands your needs.</h2>
          
          <div className="expert-type-selector">
            <button 
              className={expertType === 'Psychiatrist' ? 'active' : ''}
              onClick={() => setExpertType('Psychiatrist')}
            >
              Psychiatrist
            </button>
            <button 
              className={expertType === 'Child and Youth Expert' ? 'active' : ''}
              onClick={() => setExpertType('Child and Youth Expert')}
            >
              Child and Youth Expert
            </button>
            <button 
              className={expertType === 'Couple Therapist' ? 'active' : ''}
              onClick={() => setExpertType('Couple Therapist')}
            >
              Couple Therapist
            </button>
            <button 
              className={expertType === 'Career Counseling' ? 'active' : ''}
              onClick={() => setExpertType('Career Counseling')}
            >
              Career Counseling
            </button>
          </div>
        </div>

        <div className="employees-section">
          {loading ? (
            <div className="skeleton-loading">
              {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                <div key={i} className="skeleton-card">
                  <div className="skeleton-image"></div>
                  <div className="skeleton-content">
                    <div className="skeleton-line skeleton-title"></div>
                    <div className="skeleton-line skeleton-subtitle"></div>
                    <div className="skeleton-line skeleton-text"></div>
                    <div className="skeleton-line skeleton-text short"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmployeeList 
              employees={filteredEmployees}
              onBookClick={handleBookClick}
            />
          )}
        </div>
      </main>

      {showBookingModal && selectedEmployee && (
        <BookingModal
          employee={selectedEmployee}
          onClose={() => setShowBookingModal(false)}
          onBookingSuccess={handleBookingSuccess}
          isAuthenticated={isAuthenticated}
          user={user}
        />
      )}
    </div>
  );
}

export default Home;
